/**
 * Settings → Telegram (SPEC `telegram` § Settings → Telegram tab, §5.1 #52-#55).
 *
 * Saves **per action**, like Scrapers and Notion: connecting is a Telegram round trip, so there is
 * no draft to batch. The tab has exactly three faces, and the server decides which one — the client
 * only reads #52:
 *
 *   pending ≠ null  → the handshake (deep link + code, polling until the chat binds)
 *   connected       → the binding, with a banner when a permanent failure is stored
 *   otherwise       → the how-to and the token field
 *
 * Every sentence the user reads about a failure is the server's (§12.5); nothing here interprets a
 * Telegram error.
 */

import { useState } from 'react';
import type { TelegramStateResponse } from '@lw/shared';
import { describeError } from '../../api/client';
import { ConfirmDialog, ErrorState, Spinner } from '../../components';
import {
  useConnectTelegram,
  useDisconnectTelegram,
  useTelegramQuery,
  useTestTelegram,
} from './useTelegram';

const BOTFATHER_URL = 'https://t.me/BotFather';

/** `details: [{key, message}]` from a #53 `400`, flattened for inline rendering (§11.1). */
function fieldErrorsOf(error: unknown): Record<string, string> {
  const details = (error as { details?: unknown } | null)?.details;
  if (!Array.isArray(details)) return {};
  const errors: Record<string, string> = {};
  for (const entry of details) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { key, message } = entry as { key?: unknown; message?: unknown };
    if (typeof key === 'string' && typeof message === 'string') errors[key] = message;
  }
  return errors;
}

/* --------------------------------------------------------------- the section */

export function TelegramSection() {
  const query = useTelegramQuery();
  const state = query.data;

  if (query.isPending) return <Spinner size="lg" label="Loading Telegram settings…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load the Telegram settings"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (state === undefined) return null;

  if (state.pending !== null) return <PendingSection state={state} />;
  if (state.connected) return <ConnectedSection state={state} />;
  return <DisconnectedSection />;
}

/* ------------------------------------------------------------ 1 · disconnected */

function DisconnectedSection() {
  const connect = useConnectTelegram();
  const [token, setToken] = useState('');
  const errors = fieldErrorsOf(connect.error);

  return (
    <section className="settings-section">
      <h3 className="settings-title">Connect a bot</h3>
      <ol className="settings-steps">
        <li>
          Open{' '}
          <a href={BOTFATHER_URL} target="_blank" rel="noreferrer noopener">
            @BotFather
          </a>{' '}
          in Telegram and send <code>/newbot</code>.
        </li>
        <li>Pick a name and a username for it.</li>
        <li>Paste the token it gives you here.</li>
      </ol>

      <div className="field">
        <label className="field__label" htmlFor="telegram-token">
          Bot token
        </label>
        <input
          id="telegram-token"
          className="input"
          type="password"
          autoComplete="off"
          placeholder="123456789:AA…"
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
          }}
        />
        {errors.token !== undefined ? (
          <span className="settings-bad">{errors.token}</span>
        ) : (
          <span className="field__hint">
            The bot is yours — alerts go to your own chat with it, and nothing else can use it.
          </span>
        )}
      </div>

      <div className="row settings-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            connect.mutate(token.trim(), {
              onSuccess: () => {
                setToken('');
              },
            });
          }}
          disabled={token.trim().length === 0 || connect.isPending}
        >
          {connect.isPending ? <Spinner size="sm" /> : null}
          Connect
        </button>
      </div>

      {connect.isError && Object.keys(errors).length === 0 ? (
        <p className="form-error" role="alert">
          {describeError(connect.error)}
        </p>
      ) : null}
    </section>
  );
}

/* ---------------------------------------------------------------- 2 · pending */

function PendingSection({ state }: { state: TelegramStateResponse }) {
  const cancel = useDisconnectTelegram();
  const pending = state.pending;
  if (pending === null) return null;

  return (
    <section className="settings-section">
      <h3 className="settings-title">
        Finish in Telegram{state.bot === null ? '' : ` — ${state.bot.name}`}
      </h3>
      <p className="field__hint">
        Open the chat with the bot and press <strong>Start</strong>. The first private chat that
        sends this code is the one alerts go to.
      </p>

      <div className="row settings-actions">
        <a className="btn-primary" href={pending.deepLink} target="_blank" rel="noreferrer noopener">
          Open {state.bot === null ? 'the bot' : `@${state.bot.username}`} in Telegram
        </a>
      </div>

      <div className="field">
        <span className="field__label">Or send this code to the bot</span>
        <span className="telegram-code">{pending.code}</span>
      </div>

      <div className="row settings-actions" role="status">
        <Spinner size="sm" />
        <span className="muted">Waiting for the code…</span>
        <button
          type="button"
          className="btn-ghost btn-ghost--sm"
          onClick={() => {
            cancel.mutate();
          }}
          disabled={cancel.isPending}
        >
          Cancel
        </button>
      </div>

      {cancel.isError ? (
        <p className="form-error" role="alert">
          {describeError(cancel.error)}
        </p>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------- 3 · connected */

function ConnectedSection({ state }: { state: TelegramStateResponse }) {
  const test = useTestTelegram();
  const disconnect = useDisconnectTelegram();
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      {state.error !== null ? (
        <p className="settings-warn" role="alert">
          {state.error.message}
        </p>
      ) : null}

      <section className="settings-section">
        <h3 className="settings-title">Connection</h3>
        <dl className="settings-readonly">
          <div>
            <dt>Bot</dt>
            <dd>
              {state.bot === null ? '—' : state.bot.name}
              {state.bot === null ? null : <span className="faint"> @{state.bot.username}</span>}
            </dd>
          </div>
          <div>
            <dt>Chat</dt>
            <dd>{state.chat?.label ?? '—'}</dd>
          </div>
          <div>
            <dt>Connected</dt>
            <dd>{state.connectedAtDisplay ?? '—'}</dd>
          </div>
          <div>
            <dt>Last message sent</dt>
            <dd>{state.lastSentAtDisplay ?? 'Nothing sent yet'}</dd>
          </div>
        </dl>

        <div className="row settings-actions">
          <button
            type="button"
            className="table-action"
            onClick={() => {
              test.mutate();
            }}
            disabled={test.isPending}
          >
            {test.isPending ? <Spinner size="sm" /> : null}
            <span className="table-action__label">Send test message</span>
          </button>
          {test.isSuccess ? <span className="settings-ok">Sent.</span> : null}
          {test.isError ? <span className="settings-bad">{describeError(test.error)}</span> : null}
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            onClick={() => {
              setConfirming(true);
            }}
            disabled={disconnect.isPending}
          >
            Disconnect
          </button>
        </div>

        {!state.appBaseUrlSet ? (
          <p className="field__hint">
            Messages arrive without a <strong>View</strong> button until an app base URL is set —
            the field is on the <strong>Notion</strong> tab, under “App base URL”.
          </p>
        ) : null}

        {disconnect.isError ? (
          <p className="form-error" role="alert">
            {describeError(disconnect.error)}
          </p>
        ) : null}
      </section>

      <ConfirmDialog
        open={confirming}
        title="Disconnect Telegram?"
        message="New alerts will stop arriving in Telegram; queued ones are dropped."
        confirmLabel="Disconnect"
        destructive
        busy={disconnect.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          disconnect.mutate(undefined, {
            onSettled: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export default TelegramSection;
